<template>
  <div class="alarm-center">
    <!-- 页面标题和统计 -->
    <div class="page-header">
      <div class="stats-cards">
        <div class="stat-card total" @click="filterByStatus('')">
          <div class="stat-number">{{ statistics.total || 0 }}</div>
          <div class="stat-label">总告警</div>
        </div>
        <div class="stat-card pending" @click="filterByStatus('PENDING')">
          <div class="stat-number">{{ statistics.pending || 0 }}</div>
          <div class="stat-label">待确认</div>
        </div>
        <div class="stat-card confirmed" @click="filterByStatus('CONFIRMED')">
          <div class="stat-number">{{ statistics.confirmed || 0 }}</div>
          <div class="stat-label">已确认</div>
        </div>
        <div class="stat-card resolved" @click="filterByStatus('RESOLVED')">
          <div class="stat-number">{{ statistics.resolved || 0 }}</div>
          <div class="stat-label">已处理</div>
        </div>
      </div>
    </div>

    <!-- 筛选工具栏 -->
    <div class="filter-toolbar">
      <div class="filter-left">
        <select v-model="filters.alarmLevel" @change="loadAlarms">
          <option value="">全部级别</option>
          <option v-for="level in alarmLevels" :key="level.id" :value="level.id">
            {{ level.name }}
          </option>
        </select>
        
        <select v-model="filters.status" @change="loadAlarms">
          <option value="">全部状态</option>
          <option value="PENDING">待确认</option>
          <option value="CONFIRMED">已确认</option>
          <option value="RESOLVED">已处理</option>
          <option value="COMPLETED">已处理</option>
        </select>
        
        <button @click="refreshData" class="refresh-btn">
          刷新
        </button>
      </div>

      <div class="filter-right">
        <button 
          v-if="selectedAlarms.length > 0" 
          @click="batchConfirm" 
          class="batch-btn confirm-btn">
          批量确认 ({{ selectedAlarms.length }})
        </button>
        <button 
          v-if="selectedAlarms.length > 0" 
          @click="showBatchResolveModal = true" 
          class="batch-btn resolve-btn">
          批量处理 ({{ selectedAlarms.length }})
        </button>
      </div>
    </div>

    <!-- 告警列表 -->
    <div class="alarm-list">
      <div v-if="loading" class="loading">加载中...</div>
      
      <div v-else-if="alarms.length === 0" class="empty-state">
        <div class="empty-icon">📭</div>
        <div class="empty-text">暂无告警信息</div>
      </div>
      
      <div v-else class="alarm-table">
        <div class="table-header">
          <div class="col-select">
            <input 
              type="checkbox" 
              v-model="selectAll" 
              @change="toggleSelectAll">
          </div>
          <div class="col-level">级别</div>
          <div class="col-title">标题</div>
          <div class="col-device">设备</div>
          <div class="col-device-type">设备类型</div>
          <div class="col-location">位置</div>
          <div class="col-time">时间</div>
          <div class="col-status">状态</div>
          <div class="col-actions">操作</div>
        </div>

        <div 
          v-for="alarm in alarms" 
          :key="alarm.id" 
          class="table-row"
          :class="{ 
            'selected': selectedAlarms.includes(alarm.id),
            'highlighted': highlightAlarmId === alarm.alarmId
          }"
          :id="`alarm-row-${alarm.alarmId}`">
          
          <div class="col-select">
            <input 
              type="checkbox" 
              :value="alarm.id" 
              v-model="selectedAlarms">
          </div>
          
          <div class="col-level">
            <div class="level-display">
              <span 
                class="level-color-block" 
                :style="{ backgroundColor: alarm.alarmLevelColor || '#999999' }">
              </span>
              <span 
                class="level-text" 
                :style="{ color: alarm.alarmLevelColor || '#999999' }">
                {{ alarm.alarmLevelName || '未知' }}
              </span>
            </div>
          </div>
          
          <div class="col-title">
            <div class="alarm-title">{{ alarm.title }}</div>
            <div class="alarm-content">{{ alarm.content }}</div>
          </div>
          
          <div class="col-device">
            <div class="device-name">{{ alarm.deviceName || '未知设备' }}</div>
            <div v-if="alarm.zoneDeviceName" class="zone-info">子设备: {{ alarm.zoneDeviceName }}</div>
          </div>
          
          <div class="col-device-type">
            <div class="device-type">{{ alarm.deviceType || '未知类型' }}</div>
          </div>
          
          <div class="col-location">
            <div class="location-description">{{ alarm.locationName || alarm.locationInfo || '未设置位置' }}</div>
          </div>
          
          <div class="col-time">
            <div class="time-main">{{ formatTime(alarm.createdAt) }}</div>
            <div class="time-relative">{{ getRelativeTime(alarm.createdAt) }}</div>
          </div>
          
          <div class="col-status">
            <span 
              class="status-badge" 
              :class="`status-${alarm.status?.toLowerCase()}`">
              {{ getStatusText(alarm.status) }}
            </span>
          </div>
          
          <div class="col-actions">
            <button 
              v-if="alarm.status === 'PENDING'" 
              @click="confirmAlarm(alarm.id)" 
              class="action-btn confirm">
              确认
            </button>
            <button 
              v-if="alarm.status === 'CONFIRMED'" 
              @click="showAdvancedResolveModal(alarm)" 
              class="action-btn resolve">
              处理
            </button>
            <button @click="showDetailModal(alarm)" class="action-btn detail">
              详情
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div v-if="totalCount > 0" class="pagination">
      <div class="pagination-info">
        <span class="total-info">共 {{ totalCount }} 条记录</span>
      </div>
      
      <div class="pagination-controls">
        <button 
          @click="prevPage" 
          :disabled="currentPage === 1" 
          class="page-btn prev-btn">
          <svg class="btn-icon" viewBox="0 0 24 24" width="16" height="16">
            <path d="M15.41 16.58L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.42z" fill="currentColor"/>
          </svg>
          上一页
        </button>
        
        <div class="page-numbers">
          <span v-for="page in getPageNumbers()" :key="page" class="page-number-item">
            <button 
              v-if="page === '...'" 
              class="page-btn ellipsis" 
              disabled>
              ...
            </button>
            <button 
              v-else
              @click="goToPage(page)"
              :class="['page-btn', 'page-number', { 'active': page === currentPage }]">
              {{ page }}
            </button>
          </span>
        </div>
        
        <button 
          @click="nextPage" 
          :disabled="currentPage === totalPages" 
          class="page-btn next-btn">
          下一页
          <svg class="btn-icon" viewBox="0 0 24 24" width="16" height="16">
            <path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.42z" fill="currentColor"/>
          </svg>
        </button>
      </div>
      
      <div class="pagination-size">
        <span class="page-size-label">每页</span>
        <select v-model="pageSize" @change="changePageSize" class="page-size-select">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
        <span class="page-size-label">条</span>
      </div>
    </div>

    <!-- 处理告警对话框 -->
    <div v-if="showResolveDialog" class="modal-overlay" @click="closeResolveModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>处理告警</h3>
          <button @click="closeResolveModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>告警信息:</label>
            <div class="alarm-info">
              <div><strong>{{ currentAlarm?.title }}</strong></div>
              <div>{{ currentAlarm?.content }}</div>
            </div>
          </div>
          <div class="form-group">
            <label>处理说明:</label>
            <textarea 
              v-model="resolveNote" 
              placeholder="请输入处理说明..."
              rows="4"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeResolveModal" class="btn secondary">取消</button>
          <button @click="resolveAlarm" class="btn primary">确认处理</button>
        </div>
      </div>
    </div>

    <!-- 批量处理对话框 -->
    <div v-if="showBatchResolveModal" class="modal-overlay" @click="closeBatchResolveModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>批量处理告警</h3>
          <button @click="closeBatchResolveModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>将处理 {{ selectedAlarms.length }} 个告警</label>
          </div>
          <div class="form-group">
            <label>处理说明:</label>
            <textarea 
              v-model="batchResolveNote" 
              placeholder="请输入批量处理说明..."
              rows="4"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeBatchResolveModal" class="btn secondary">取消</button>
          <button @click="batchResolve" class="btn primary">确认处理</button>
        </div>
      </div>
    </div>

    <!-- 高级处理告警对话框 -->
    <div v-if="showAdvancedResolveDialog" class="modal-overlay" @click="closeAdvancedResolveModal">
      <div class="modal large" @click.stop>
        <div class="modal-header">
          <h3>处理告警</h3>
          <button @click="closeAdvancedResolveModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>告警信息:</label>
            <div class="alarm-info">
              <div><strong>{{ currentAlarm?.title }}</strong></div>
              <div>{{ currentAlarm?.content }}</div>
              <div class="alarm-meta">
                <span class="device-info">设备：{{ currentAlarm?.device?.deviceName }}</span>
                <span class="location-info">位置：{{ currentAlarm?.locationName || currentAlarm?.device?.locationName || currentAlarm?.locationInfo || '未设置' }}</span>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label>处理动作: <span class="required">*</span></label>
            <select v-model="resolveActionType" class="action-select">
              <option value="">请选择处理动作</option>
              <option value="IN_PROGRESS">处理中 - 正在处理中，暂未完成</option>
              <option value="TEST_ALARM">测试告警 - 设备功能测试产生的告警</option>
              <option value="FALSE_ALARM">误报 - 设备误报或环境因素导致</option>
              <option value="REAL_ALARM">真实告警 - 真实的安全事件，已处理</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>处理说明: <span class="required">*</span></label>
            <textarea 
              v-model="advancedResolveNote" 
              placeholder="请详细描述处理过程和结果..."
              rows="4"
              class="full-width"></textarea>
          </div>
          
          <div class="form-group">
            <label>现场照片: <small>(最多5张，支持JPG、PNG、GIF、WebP格式，单张不超过5MB)</small></label>
            <div class="photo-upload-area">
              <div class="upload-zone" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleFileDrop">
                <input 
                  ref="fileInput" 
                  type="file" 
                  multiple 
                  accept="image/*" 
                  @change="handleFileSelect" 
                  style="display: none;">
                <div class="upload-hint">
                  <div class="upload-icon">📷</div>
                  <div>点击或拖拽上传照片</div>
                </div>
              </div>
              
              <div v-if="selectedFiles.length > 0" class="selected-files">
                <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                  <img :src="getFilePreview(file)" alt="预览" class="file-preview">
                  <div class="file-info">
                    <div class="file-name">{{ file.name }}</div>
                    <div class="file-size">{{ formatFileSize(file.size) }}</div>
                  </div>
                  <button @click="removeFile(index)" class="remove-file">×</button>
                </div>
              </div>
              
              <div v-if="uploadedPhotos.length > 0" class="uploaded-photos">
                <div v-for="(url, index) in uploadedPhotos" :key="index" class="uploaded-photo">
                  <img :src="url" alt="已上传" class="photo-preview">
                  <button @click="removeUploadedPhoto(index)" class="remove-photo">×</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeAdvancedResolveModal" class="btn secondary">取消</button>
          <button @click="uploadPhotosAndResolve" class="btn primary" :disabled="!resolveActionType || !advancedResolveNote">
            <span v-if="uploading">上传中...</span>
            <span v-else>确认处理</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 告警详情对话框 -->
    <div v-if="showDetailDialog" class="modal-overlay" @click="closeDetailModal">
      <div class="modal large" @click.stop>
        <div class="modal-header">
          <h3>告警详情</h3>
          <button @click="closeDetailModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div v-if="currentAlarm" class="alarm-detail">
            <div class="detail-section">
              <h4>基本信息</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>告警标题:</label>
                  <span>{{ currentAlarm.title }}</span>
                </div>
                <div class="detail-item">
                  <label>告警级别:</label>
                  <span class="level-text" :style="{ color: getAlarmLevelColor(currentAlarm.alarmLevelId) }">
                    {{ getAlarmLevelName(currentAlarm.alarmLevelId) }}
                  </span>
                </div>
                <div class="detail-item">
                  <label>告警状态:</label>
                  <span 
                    class="status-badge" 
                    :class="`status-${currentAlarm.status?.toLowerCase()}`">
                    {{ getStatusText(currentAlarm.status) }}
                  </span>
                </div>
                <div class="detail-item">
                  <label>发生时间:</label>
                  <span>{{ formatDateTime(currentAlarm.createdAt) }}</span>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h4>告警内容</h4>
              <div class="alarm-content-detail">
                {{ currentAlarm.content }}
              </div>
            </div>

            <div class="detail-section">
              <h4>设备信息</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>设备名称:</label>
                  <span>{{ currentAlarm.deviceName || '未知' }}</span>
                </div>
                <div class="detail-item">
                  <label>所在位置:</label>
                  <span>{{ currentAlarm.locationName || currentAlarm.device?.locationName || currentAlarm.locationInfo || '未知' }}</span>
                </div>
              </div>
            </div>

            <!-- 处理时间轴 -->
            <div v-if="currentAlarm.processingRecords && currentAlarm.processingRecords.length > 0" class="detail-section">
              <h4>处理记录</h4>
              <div class="processing-timeline">
                <div 
                  v-for="(record, index) in currentAlarm.processingRecords" 
                  :key="record.id"
                  class="timeline-item">
                  <div class="timeline-marker">
                    <div class="timeline-icon" :class="`icon-${record.operationType?.toLowerCase()}`">
                      <span v-if="record.operationType === 'CONFIRM'">✓</span>
                      <span v-else-if="record.operationType === 'PROCESS'">🔧</span>
                      <span v-else-if="record.operationType === 'CLOSE'">🔒</span>
                      <span v-else-if="record.operationType === 'REOPEN'">🔓</span>
                      <span v-else>📝</span>
                    </div>
                  </div>
                  
                  <div class="timeline-content">
                    <div class="timeline-header">
                      <div class="timeline-title">
                        {{ getOperationTypeText(record.operationType) }}
                        <span v-if="record.processingAction" class="processing-action-badge" :class="`action-${record.processingAction?.toLowerCase()}`">
                          {{ getResolveActionText(record.processingAction) }}
                        </span>
                      </div>
                      <div class="timeline-meta">
                        <span class="timeline-user">{{ record.processedByName || record.processedBy }}</span>
                        <span class="timeline-time">{{ formatDateTime(record.processedAt) }}</span>
                      </div>
                    </div>
                    
                    <div v-if="record.processingNote" class="timeline-note">
                      {{ record.processingNote }}
                    </div>
                    
                    <!-- 处理照片 -->
                    <div v-if="record.processingPhotos && getResolvePhotos(record.processingPhotos).length > 0" class="timeline-photos">
                      <div class="photo-grid">
                        <div 
                          v-for="(photo, photoIndex) in getResolvePhotos(record.processingPhotos)" 
                          :key="photoIndex"
                          class="photo-thumbnail small"
                          @click="openPhotoViewer(getResolvePhotos(record.processingPhotos), photoIndex)">
                          <img 
                            :src="photo" 
                            :alt="`处理照片 ${photoIndex + 1}`" 
                            @error="handleImageError($event)" />
                          <div class="photo-overlay">
                            <span class="zoom-icon">🔍</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 无处理记录时的提示 -->
            <div v-else class="detail-section">
              <h4>处理记录</h4>
              <div class="empty-processing">
                <div class="empty-icon">📝</div>
                <div class="empty-text">暂无处理记录</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="footer-left">
            <button @click="closeDetailModal" class="btn secondary">关闭</button>
          </div>
          <div class="footer-right">
            <!-- 待确认状态：可以确认 -->
            <button 
              v-if="currentAlarm?.status === 'PENDING'" 
              @click="confirmAlarm(currentAlarm.id)" 
              class="btn primary">
              确认告警
            </button>
            
            <!-- 已确认状态：可以处理 -->
            <button 
              v-if="currentAlarm?.status === 'CONFIRMED'" 
              @click="showAdvancedResolveModal(currentAlarm)" 
              class="btn primary">
              处理告警
            </button>
            
            <!-- 处理中/已解决状态：可以继续处理、关闭或重开 -->
            <div v-if="currentAlarm?.status === 'IN_PROGRESS' || currentAlarm?.status === 'RESOLVED' || currentAlarm?.status === 'COMPLETED'" class="action-buttons">
              <button 
                @click="showAdvancedResolveModal(currentAlarm)" 
                class="btn primary">
                继续处理
              </button>
              <button 
                v-if="currentAlarm?.status !== 'CLOSED'" 
                @click="showCloseModal(currentAlarm)" 
                class="btn warning">
                关闭告警
              </button>
            </div>
            
            <!-- 已关闭状态：可以重新打开 -->
            <button 
              v-if="currentAlarm?.status === 'CLOSED'" 
              @click="showReopenModal(currentAlarm)" 
              class="btn info">
              重新打开
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 关闭告警对话框 -->
    <div v-if="showCloseDialog" class="modal-overlay" @click="closeCloseModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>关闭告警</h3>
          <button @click="closeCloseModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>告警信息:</label>
            <div class="alarm-info">
              <div><strong>{{ currentAlarm?.title }}</strong></div>
              <div>{{ currentAlarm?.content }}</div>
            </div>
          </div>
          
          <div class="form-group">
            <label>关闭原因: <span class="required">*</span></label>
            <textarea 
              v-model="closeNote" 
              placeholder="请说明关闭告警的原因..."
              rows="3"
              class="full-width"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeCloseModal" class="btn secondary">取消</button>
          <button @click="closeAlarm" class="btn warning" :disabled="!closeNote.trim()">
            确认关闭
          </button>
        </div>
      </div>
    </div>

    <!-- 重新打开告警对话框 -->
    <div v-if="showReopenDialog" class="modal-overlay" @click="closeReopenModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>重新打开告警</h3>
          <button @click="closeReopenModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>告警信息:</label>
            <div class="alarm-info">
              <div><strong>{{ currentAlarm?.title }}</strong></div>
              <div>{{ currentAlarm?.content }}</div>
            </div>
          </div>
          
          <div class="form-group">
            <label>重开原因: <span class="required">*</span></label>
            <textarea 
              v-model="reopenNote" 
              placeholder="请说明重新打开告警的原因..."
              rows="3"
              class="full-width"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeReopenModal" class="btn secondary">取消</button>
          <button @click="reopenAlarm" class="btn info" :disabled="!reopenNote.trim()">
            确认重开
          </button>
        </div>
      </div>
    </div>

    <!-- 通知系统 -->
    <div class="notification-container">
      <transition-group name="notification" tag="div">
        <div 
          v-for="notification in notifications" 
          :key="notification.id"
          :class="['notification', `notification-${notification.type}`, { 'notification-visible': notification.visible }]"
          @click="removeNotification(notification.id)">
          <div class="notification-icon">
            <span v-if="notification.type === 'success'">✓</span>
            <span v-else-if="notification.type === 'error'">✕</span>
            <span v-else-if="notification.type === 'warning'">⚠</span>
            <span v-else>ℹ</span>
          </div>
          <div class="notification-content">
            <div class="notification-message">{{ notification.message }}</div>
          </div>
          <div class="notification-close" @click.stop="removeNotification(notification.id)">×</div>
        </div>
      </transition-group>
    </div>

    <!-- 照片查看器 -->
    <div v-if="showPhotoViewer" class="photo-viewer-overlay" @click="closePhotoViewer">
      <div class="photo-viewer" @click.stop>
        <div class="photo-viewer-header">
          <div class="photo-counter">{{ currentPhotoIndex + 1 }} / {{ photoList.length }}</div>
          <button @click="closePhotoViewer" class="photo-close-btn">×</button>
        </div>
        
        <div class="photo-viewer-content">
          <button 
            v-if="photoList.length > 1 && currentPhotoIndex > 0" 
            @click="prevPhoto" 
            class="photo-nav-btn prev-btn">
            ‹
          </button>
          
          <div class="photo-container">
            <img 
              :src="photoList[currentPhotoIndex]" 
              :alt="`照片 ${currentPhotoIndex + 1}`"
              class="photo-main"
              @load="onPhotoLoad"
              @error="onPhotoError" />
          </div>
          
          <button 
            v-if="photoList.length > 1 && currentPhotoIndex < photoList.length - 1" 
            @click="nextPhoto" 
            class="photo-nav-btn next-btn">
            ›
          </button>
        </div>
        
        <div v-if="photoList.length > 1" class="photo-thumbnails">
          <div 
            v-for="(photo, index) in photoList" 
            :key="index"
            :class="['thumbnail-item', { 'active': index === currentPhotoIndex }]"
            @click="currentPhotoIndex = index">
            <img :src="photo" :alt="`缩略图 ${index + 1}`" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/utils/api'
import API_CONFIG from '@/config/api.js'
import websocketService from '@/utils/websocket'

export default {
  name: 'AlarmCenter',
  setup() {
    const route = useRoute()
    const router = useRouter()
    
    // 响应式数据
    const alarms = ref([])
    const alarmLevels = ref([])
    const statistics = ref({})
    const loading = ref(false)
    const currentPage = ref(1)
    const pageSize = ref(20)
    const totalCount = ref(0)
    const selectedAlarms = ref([])
    const highlightAlarmId = ref(null)
    
    // 筛选条件
    const filters = reactive({
      alarmLevel: '',
      status: '',
      familyId: ''
    })
    
    // 对话框状态
    const showResolveDialog = ref(false)
    const showBatchResolveModal = ref(false)
    const showDetailDialog = ref(false)
    const showAdvancedResolveDialog = ref(false)
    const showCloseDialog = ref(false)
    const showReopenDialog = ref(false)
    const currentAlarm = ref(null)
    const resolveNote = ref('')
    const batchResolveNote = ref('')
    
    // 高级处理相关
    const advancedResolveNote = ref('')
    const resolveActionType = ref('')
    const closeNote = ref('')
    const reopenNote = ref('')
    const selectedFiles = ref([])
    const uploadedPhotos = ref([])
    const uploading = ref(false)
    
    // 通知系统
    const notifications = ref([])
    let notificationId = 0
    
    // 照片查看器
    const showPhotoViewer = ref(false)
    const photoList = ref([])
    const currentPhotoIndex = ref(0)
    
    // 计算属性
    const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))
    const selectAll = ref(false)
    
    // 通知方法
    const showNotification = (message, type = 'info', duration = 4000) => {
      const id = ++notificationId
      const notification = {
        id,
        message,
        type,
        visible: false
      }
      
      notifications.value.push(notification)
      
      // 延迟显示以触发动画
      nextTick(() => {
        notification.visible = true
      })
      
      // 自动移除
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }
    
    const removeNotification = (id) => {
      const index = notifications.value.findIndex(n => n.id === id)
      if (index > -1) {
        notifications.value[index].visible = false
        setTimeout(() => {
          notifications.value.splice(index, 1)
        }, 300) // 等待动画完成
      }
    }
    
    // 照片查看器方法
    const openPhotoViewer = (photos, index = 0) => {
      photoList.value = photos
      currentPhotoIndex.value = index
      showPhotoViewer.value = true
      document.body.style.overflow = 'hidden' // 防止背景滚动
    }
    
    const closePhotoViewer = () => {
      showPhotoViewer.value = false
      photoList.value = []
      currentPhotoIndex.value = 0
      document.body.style.overflow = '' // 恢复背景滚动
    }
    
    const prevPhoto = () => {
      if (currentPhotoIndex.value > 0) {
        currentPhotoIndex.value--
      }
    }
    
    const nextPhoto = () => {
      if (currentPhotoIndex.value < photoList.value.length - 1) {
        currentPhotoIndex.value++
      }
    }
    
    const onPhotoLoad = () => {
      // 照片加载完成
    }
    
    const onPhotoError = () => {
      showNotification('照片加载失败', 'error')
    }
    
    // 处理信息方法
    const getResolvePhotos = (photosJson) => {
      if (!photosJson) return []
      try {
        const photos = typeof photosJson === 'string' ? JSON.parse(photosJson) : photosJson
        // 获取认证信息
        const token = localStorage.getItem('token')
        const user = localStorage.getItem('user')
        const family = localStorage.getItem('family')
        
        // 确保图片URL使用正确的后端地址并添加认证参数
        return photos.map(photo => {
          let url = API_CONFIG.getImageUrl(photo)
          
          // 为图片URL添加认证参数
          const urlObj = new URL(url)
          if (token) {
            urlObj.searchParams.set('token', token)
          }
          if (user) {
            const userData = JSON.parse(user)
            urlObj.searchParams.set('userId', userData.id)
          }
          if (family && family !== 'null') {
            try {
              const familyData = JSON.parse(family)
              if (familyData && familyData.id) {
                urlObj.searchParams.set('familyId', familyData.id)
              }
            } catch (error) {
              console.error('解析家庭信息失败:', error)
            }
          }
          
          return urlObj.toString()
        })
      } catch (error) {
        console.error('解析处理照片失败:', error)
        return []
      }
    }
    
    const getResolveActionText = (action) => {
      const actionMap = {
        'IN_PROGRESS': '处理中',
        'TEST_ALARM': '测试告警',
        'FALSE_ALARM': '误报',
        'REAL_ALARM': '真实告警'
      }
      return actionMap[action] || action
    }
    
    const getOperationTypeText = (operationType) => {
      const typeMap = {
        'CONFIRM': '确认告警',
        'PROCESS': '处理告警',
        'CLOSE': '关闭告警',
        'REOPEN': '重新打开',
        'ESCALATE': '升级处理'
      }
      return typeMap[operationType] || operationType
    }
    
    const handleImageError = (event) => {
      // 图片加载失败时的处理
      event.target.style.display = 'none'
      console.warn('图片加载失败:', event.target.src)
    }
    
    // 方法
    const loadAlarms = async () => {
      loading.value = true
      try {
        const params = {
          page: currentPage.value,
          size: pageSize.value,
          ...filters
        }
        
        const response = await api.get('/alarms', { params })
        if (response && response.success) {
          alarms.value = response.data
          totalCount.value = response.total
        }
      } catch (error) {
        console.error('获取告警列表失败:', error)
      } finally {
        loading.value = false
      }
    }
    
    const loadAlarmLevels = async () => {
      try {
        const response = await api.get('/alarms/levels')
        if (response && response.success) {
          alarmLevels.value = response.data
        }
      } catch (error) {
        console.error('获取告警级别失败:', error)
      }
    }
    
    const loadStatistics = async () => {
      try {
        const response = await api.get('/alarms/statistics')
        if (response && response.success) {
          statistics.value = response.data
        }
      } catch (error) {
        console.error('获取统计信息失败:', error)
      }
    }
    
    const refreshData = async () => {
      await Promise.all([
        loadAlarms(),
        loadStatistics()
      ])
      
      // 更新侧边栏badge数量
      updateSidebarBadge()
    }
    
    // 更新侧边栏badge
    const updateSidebarBadge = async () => {
      try {
        if (window.updatePendingAlarmsCount) {
          const response = await api.get('/alarms/pending-count')
          if (response.success) {
            window.updatePendingAlarmsCount(response.data || 0)
          }
        }
      } catch (error) {
        console.error('更新badge失败:', error)
      }
    }
    
    const confirmAlarm = async (alarmId) => {
      try {
        const response = await api.post(`/alarms/${alarmId}/confirm`)
        if (response && response.success) {
          showNotification('告警确认成功', 'success')
          showDetailDialog.value = false  // 关闭详情弹窗
          await refreshData()
        } else {
          showNotification(response?.message || '确认失败', 'error')
        }
      } catch (error) {
        console.error('确认告警失败:', error)
        showNotification('确认告警失败', 'error')
      }
    }
    
    const showResolveModal = (alarm) => {
      currentAlarm.value = alarm
      resolveNote.value = ''
      showResolveDialog.value = true
      showDetailDialog.value = false
    }
    
    const closeResolveModal = () => {
      showResolveDialog.value = false
      currentAlarm.value = null
      resolveNote.value = ''
    }
    
    const resolveAlarm = async () => {
      if (!resolveNote.value.trim()) {
        showNotification('请输入处理说明', 'warning')
        return
      }
      
      try {
        const response = await api.post(`/alarms/${currentAlarm.value.id}/resolve`, {
          resolveNote: resolveNote.value
        })
        if (response && response.success) {
          showNotification('告警处理成功', 'success')
          closeResolveModal()
          await refreshData()
        } else {
          showNotification(response?.message || '处理失败', 'error')
        }
      } catch (error) {
        console.error('处理告警失败:', error)
        showNotification('处理告警失败', 'error')
      }
    }
    
    const batchConfirm = async () => {
      if (selectedAlarms.value.length === 0) return
      
      try {
        const response = await api.post('/alarms/batch-confirm', {
          alarmIds: selectedAlarms.value
        })
        if (response && response.success) {
          showNotification(response.message, 'error')
          selectedAlarms.value = []
          await refreshData()
        } else {
          showNotification(response?.message || '批量确认失败', 'error')
        }
      } catch (error) {
        console.error('批量确认失败:', error)
        showNotification('批量确认失败', 'error')
      }
    }
    
    const closeBatchResolveModal = () => {
      showBatchResolveModal.value = false
      batchResolveNote.value = ''
    }
    
    const batchResolve = async () => {
      if (!batchResolveNote.value.trim()) {
        showNotification('请输入处理说明', 'warning')
        return
      }
      
      try {
        const response = await api.post('/alarms/batch-resolve', {
          alarmIds: selectedAlarms.value,
          resolveNote: batchResolveNote.value
        })
        if (response && response.success) {
          showNotification(response.message, 'error')
          selectedAlarms.value = []
          closeBatchResolveModal()
          await refreshData()
        } else {
          showNotification(response?.message || '批量处理失败', 'error')
        }
      } catch (error) {
        console.error('批量处理失败:', error)
        showNotification('批量处理失败', 'error')
      }
    }
    
    const showDetailModal = async (alarm) => {
      try {
        // 从后端获取告警详情，包括处理记录
        const response = await api.get(`/alarms/${alarm.id}`)
        if (response && response.success) {
          currentAlarm.value = response.data
        } else {
          currentAlarm.value = alarm
        }
      } catch (error) {
        console.error('获取告警详情失败:', error)
        currentAlarm.value = alarm
      }
      showDetailDialog.value = true
    }
    
    const closeDetailModal = () => {
      showDetailDialog.value = false
      currentAlarm.value = null
    }
    
    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedAlarms.value = alarms.value.map(alarm => alarm.id)
      } else {
        selectedAlarms.value = []
      }
    }
    
    const filterByStatus = (status) => {
      filters.status = status
      currentPage.value = 1
      loadAlarms()
    }
    
    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        loadAlarms()
      }
    }
    
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
        loadAlarms()
      }
    }
    
    const goToPage = (page) => {
      if (page !== currentPage.value && page >= 1 && page <= totalPages.value) {
        currentPage.value = page
        loadAlarms()
      }
    }
    
    const changePageSize = () => {
      currentPage.value = 1 // 重置到第一页
      loadAlarms()
    }
    
    // 生成页码数组（显示逻辑）
    const getPageNumbers = () => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      
      if (total <= 7) {
        // 总页数少于等于7页时，显示所有页码
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        // 总页数大于7页时，使用省略号
        pages.push(1) // 始终显示第一页
        
        if (current <= 4) {
          // 当前页在前4页
          for (let i = 2; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          // 当前页在后4页
          pages.push('...')
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          // 当前页在中间
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        }
      }
      
      return pages
    }
    
    const formatTime = (dateTime) => {
      if (!dateTime) return ''
      return new Date(dateTime).toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    const formatDateTime = (dateTime) => {
      if (!dateTime) return ''
      return new Date(dateTime).toLocaleString('zh-CN')
    }
    
    const getRelativeTime = (dateTime) => {
      if (!dateTime) return ''
      const now = new Date()
      const time = new Date(dateTime)
      const diff = now - time
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)
      
      if (days > 0) return `${days}天前`
      if (hours > 0) return `${hours}小时前`
      if (minutes > 0) return `${minutes}分钟前`
      return '刚刚'
    }
    
    const getStatusText = (status) => {
      const statusMap = {
        'PENDING': '待确认',
        'CONFIRMED': '已确认',
        'IN_PROGRESS': '处理中',
        'RESOLVED': '已处理',
        'COMPLETED': '已处理',
        'CLOSED': '已关闭'
      }
      return statusMap[status] || status
    }
    
    const getAlarmLevelName = (levelId) => {
      const level = alarmLevels.value.find(l => l.id === levelId)
      return level ? level.name : '未知'
    }
    
    const getAlarmLevelColor = (levelId) => {
      const level = alarmLevels.value.find(l => l.id === levelId)
      return level ? level.color : '#gray'
    }
    
    // 高级处理对话框相关方法
    const showAdvancedResolveModal = (alarm) => {
      currentAlarm.value = alarm
      advancedResolveNote.value = ''
      resolveActionType.value = ''
      selectedFiles.value = []
      uploadedPhotos.value = []
      showAdvancedResolveDialog.value = true
      showDetailDialog.value = false
    }
    
    const closeAdvancedResolveModal = () => {
      showAdvancedResolveDialog.value = false
      currentAlarm.value = null
      advancedResolveNote.value = ''
      resolveActionType.value = ''
      selectedFiles.value = []
      uploadedPhotos.value = []
      uploading.value = false
    }
    
    // 关闭告警相关方法
    const showCloseModal = (alarm) => {
      currentAlarm.value = alarm
      closeNote.value = ''
      showCloseDialog.value = true
    }
    
    const closeCloseModal = () => {
      showCloseDialog.value = false
      currentAlarm.value = null
      closeNote.value = ''
    }
    
    const closeAlarm = async () => {
      if (!closeNote.value.trim()) {
        showNotification('请填写关闭原因', 'warning')
        return
      }
      
      try {
        const response = await api.post(`/alarms/${currentAlarm.value.id}/close`, {
          closeNote: closeNote.value
        })
        
        if (response && response.success) {
          showNotification('告警关闭成功', 'success')
          closeCloseModal()
          closeDetailModal()
          await refreshData()
        } else {
          showNotification(response?.message || '关闭失败', 'error')
        }
      } catch (error) {
        console.error('关闭告警失败:', error)
        showNotification('关闭告警失败: ' + (error.message || '未知错误'), 'error')
      }
    }
    
    // 重新打开告警相关方法
    const showReopenModal = (alarm) => {
      currentAlarm.value = alarm
      reopenNote.value = ''
      showReopenDialog.value = true
    }
    
    const closeReopenModal = () => {
      showReopenDialog.value = false
      currentAlarm.value = null
      reopenNote.value = ''
    }
    
    const reopenAlarm = async () => {
      if (!reopenNote.value.trim()) {
        showNotification('请填写重开原因', 'warning')
        return
      }
      
      try {
        const response = await api.post(`/alarms/${currentAlarm.value.id}/reopen`, {
          reopenNote: reopenNote.value
        })
        
        if (response && response.success) {
          showNotification('告警重新打开成功', 'success')
          closeReopenModal()
          closeDetailModal()
          await refreshData()
        } else {
          showNotification(response?.message || '重新打开失败', 'error')
        }
      } catch (error) {
        console.error('重新打开告警失败:', error)
        showNotification('重新打开告警失败: ' + (error.message || '未知错误'), 'error')
      }
    }
    
    const triggerFileInput = () => {
      const fileInput = document.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.click()
      }
    }
    
    const handleFileSelect = (event) => {
      const files = Array.from(event.target.files)
      addFiles(files)
    }
    
    const handleFileDrop = (event) => {
      const files = Array.from(event.dataTransfer.files)
      addFiles(files)
    }
    
    const addFiles = (files) => {
      // 检查文件数量限制
      if (selectedFiles.value.length + files.length > 5) {
        showNotification('最多只能上传5张照片', 'warning')
        return
      }
      
      // 验证文件类型和大小
      const validFiles = files.filter(file => {
        // 检查文件类型
        if (!file.type.startsWith('image/')) {
          showNotification(`文件 ${file.name} 不是图片格式`, 'error')
          return false
        }
        
        // 检查文件大小 (5MB)
        if (file.size > 5 * 1024 * 1024) {
          showNotification(`文件 ${file.name} 大小超过5MB`, 'error')
          return false
        }
        
        return true
      })
      
      selectedFiles.value.push(...validFiles)
    }
    
    const removeFile = (index) => {
      selectedFiles.value.splice(index, 1)
    }
    
    const removeUploadedPhoto = (index) => {
      uploadedPhotos.value.splice(index, 1)
    }
    
    const getFilePreview = (file) => {
      return URL.createObjectURL(file)
    }
    
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
    
    const uploadPhotosAndResolve = async () => {
      if (!resolveActionType.value || !advancedResolveNote.value.trim()) {
        showNotification('请选择处理动作并填写处理说明', 'warning')
        return
      }
      
      uploading.value = true
      let photoUrls = [...uploadedPhotos.value]
      
      try {
        // 如果有新选择的文件，先上传
        if (selectedFiles.value.length > 0) {
          const formData = new FormData()
          selectedFiles.value.forEach(file => {
            formData.append('files', file)
          })
          formData.append('alarmId', currentAlarm.value.id)
          
          const uploadResponse = await api.post('/files/upload/alarm-photos', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          
          if (uploadResponse && uploadResponse.success) {
            photoUrls.push(...uploadResponse.data.urls)
          } else {
            throw new Error(uploadResponse?.message || '照片上传失败')
          }
        }
        
        // 提交告警处理
        const response = await api.post(`/alarms/${currentAlarm.value.id}/resolve-advanced`, {
          resolveNote: advancedResolveNote.value,
          resolveAction: resolveActionType.value,
          resolvePhotos: JSON.stringify(photoUrls)
        })
        
        if (response && response.success) {
          showNotification('告警处理成功', 'success')
          closeAdvancedResolveModal()
          await refreshData()
        } else {
          showNotification(response?.message || '处理失败', 'error')
        }
      } catch (error) {
        console.error('告警处理失败:', error)
        showNotification('告警处理失败: ' + (error.message || '未知错误'), 'error')
      } finally {
        uploading.value = false
      }
    }
    
    // 高亮指定告警
    const highlightAlarm = async (alarmId) => {
      if (!alarmId) return
      
      highlightAlarmId.value = alarmId
      
      // 等待数据加载完成后滚动到指定告警
      await nextTick()
      
      const targetElement = document.getElementById(`alarm-row-${alarmId}`)
      if (targetElement) {
        targetElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        })
        
        // 3秒后移除高亮
        setTimeout(() => {
          highlightAlarmId.value = null
          // 清除URL参数
          router.replace({ query: {} })
        }, 3000)
      }
    }
    
    // 监听路由变化
    watch(
      () => route.query.highlight,
      (newHighlight) => {
        if (newHighlight) {
          highlightAlarm(newHighlight)
        }
      },
      { immediate: true }
    )
    
    // 初始化家庭ID
    const initializeFamilyId = () => {
      const family = localStorage.getItem('family')
      if (family && family !== 'null') {
        try {
          const familyData = JSON.parse(family)
          if (familyData && familyData.id) {
            filters.familyId = familyData.id
          } else {
            console.warn('家庭信息无效，跳过家庭ID设置')
            filters.familyId = null
          }
        } catch (error) {
          console.error('解析家庭信息失败:', error)
          filters.familyId = null
        }
      } else {
        console.warn('未找到家庭信息，跳过家庭ID设置')
        filters.familyId = null
      }
    }
    
    // WebSocket告警通知处理
    const handleAlarmNotification = (event) => {
      const alarmNotification = event.detail
      console.log('AlarmCenter收到新告警通知:', alarmNotification)
      
      // 立即更新统计数据
      loadStatistics()
      
      // 如果当前在第一页且没有过滤条件，直接在列表顶部添加新告警
      if (currentPage.value === 1 && !filters.status && !filters.alarmLevel) {
        addNewAlarmToList(alarmNotification)
      } else {
        // 否则刷新整个列表
        loadAlarms()
      }
      
      // 高亮显示新告警
      if (alarmNotification.alarmId) {
        setTimeout(() => {
          highlightAlarm(alarmNotification.alarmId)
        }, 300)
      }
      
      // 更新侧边栏badge数量
      updateSidebarBadge()
    }
    
    // 在列表顶部添加新告警（避免完整刷新）
    const addNewAlarmToList = (alarmNotification) => {
      const newAlarm = {
        id: `new-${Date.now()}`, // 临时ID
        alarmId: alarmNotification.alarmId,
        title: alarmNotification.title,
        content: alarmNotification.content,
        deviceName: alarmNotification.deviceName,
        deviceType: '传感器', // 默认类型
        locationName: alarmNotification.location,
        alarmLevelName: getLevelNameFromId(alarmNotification.alarmLevel),
        alarmLevelColor: getLevelColorFromId(alarmNotification.alarmLevel),
        status: alarmNotification.status || 'PENDING',
        createdAt: new Date().toISOString()
      }
      
      // 在列表顶部插入新告警
      alarms.value.unshift(newAlarm)
      
      // 如果列表超过页面大小，移除最后一个
      if (alarms.value.length > pageSize.value) {
        alarms.value.pop()
      }
      
      console.log('新告警已添加到列表顶部:', newAlarm)
    }
    
    // 根据级别ID获取级别名称
    const getLevelNameFromId = (levelId) => {
      const levelMap = { '4': '紧急', '3': '高', '2': '中', '1': '低' }
      return levelMap[levelId] || '未知'
    }
    
    // 根据级别ID获取级别颜色
    const getLevelColorFromId = (levelId) => {
      const colorMap = { '4': '#ff4d4f', '3': '#fa8c16', '2': '#fadb14', '1': '#52c41a' }
      return colorMap[levelId] || '#8c8c8c'
    }

    // 生命周期
    onMounted(async () => {
      // 初始化家庭ID
      initializeFamilyId()
      
      await loadAlarmLevels()
      await refreshData()
      
      // 检查是否需要高亮特定告警
      if (route.query.highlight) {
        highlightAlarm(route.query.highlight)
      }
      
      // 添加WebSocket告警通知监听器
      window.addEventListener('alarm-notification', handleAlarmNotification)
    })
    
    onUnmounted(() => {
      // 移除WebSocket告警通知监听器
      window.removeEventListener('alarm-notification', handleAlarmNotification)
    })
    
    return {
      alarms,
      alarmLevels,
      statistics,
      loading,
      currentPage,
      pageSize,
      totalCount,
      totalPages,
      selectedAlarms,
      filters,
      showResolveDialog,
      showBatchResolveModal,
      showDetailDialog,
      showAdvancedResolveDialog,
      showCloseDialog,
      showReopenDialog,
      currentAlarm,
      resolveNote,
      batchResolveNote,
      advancedResolveNote,
      resolveActionType,
      closeNote,
      reopenNote,
      selectedFiles,
      uploadedPhotos,
      uploading,
      selectAll,
      notifications,
      showNotification,
      removeNotification,
      showPhotoViewer,
      photoList,
      currentPhotoIndex,
      openPhotoViewer,
      closePhotoViewer,
      prevPhoto,
      nextPhoto,
      onPhotoLoad,
      onPhotoError,
      getResolvePhotos,
      getResolveActionText,
      getOperationTypeText,
      handleImageError,
      loadAlarms,
      refreshData,
      confirmAlarm,
      showResolveModal,
      closeResolveModal,
      resolveAlarm,
      batchConfirm,
      closeBatchResolveModal,
      batchResolve,
      showDetailModal,
      closeDetailModal,
      showAdvancedResolveModal,
      closeAdvancedResolveModal,
      showCloseModal,
      closeCloseModal,
      closeAlarm,
      showReopenModal,
      closeReopenModal,
      reopenAlarm,
      triggerFileInput,
      handleFileSelect,
      handleFileDrop,
      removeFile,
      removeUploadedPhoto,
      getFilePreview,
      formatFileSize,
      uploadPhotosAndResolve,
      toggleSelectAll,
      filterByStatus,
      prevPage,
      nextPage,
      goToPage,
      changePageSize,
      getPageNumbers,
      formatTime,
      formatDateTime,
      getRelativeTime,
      getStatusText,
      getAlarmLevelName,
      getAlarmLevelColor,
      initializeFamilyId,
      $apiConfig: API_CONFIG
    }
  }
}
</script>

<style scoped>
.alarm-center {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;
}

.stats-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  flex: 1;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
}

.stat-card.total .stat-number { color: #666; }
.stat-card.pending .stat-number { color: #fa8c16; }
.stat-card.confirmed .stat-number { color: #1890ff; }
.stat-card.resolved .stat-number { color: #52c41a; }

.stat-label {
  color: #666;
  font-size: 14px;
}

.filter-toolbar {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-left {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filter-left select {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
}

.refresh-btn {
  padding: 8px 16px;
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.refresh-btn:hover {
  background: #40a9ff;
}

.filter-right {
  display: flex;
  gap: 8px;
}

.batch-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.confirm-btn {
  background: #1890ff;
  color: white;
}

.resolve-btn {
  background: #52c41a;
  color: white;
}

.alarm-list {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.loading {
  padding: 40px;
  text-align: center;
  color: #666;
}

.empty-state {
  padding: 60px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  color: #666;
  font-size: 16px;
}

.alarm-table {
  width: 100%;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 40px 80px 1fr 220px 160px 1fr 120px 80px 180px;
  gap: 12px;
  padding: 12px;
  align-items: center;
  width: 100%;
  text-align: center;
}

.table-header {
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  color: #666;
}

.table-row {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.table-row:hover {
  background: #fafafa;
}

.table-row.selected {
  background: #e6f7ff;
}

.table-row.highlighted {
  /* 移除黄色高亮样式，保持简洁的表格外观 */
}

/* 移除高亮动画样式 */

.level-display {
  display: flex;
  align-items: center;
  gap: 6px;
}

.level-color-block {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

.level-text {
  font-size: 13px;
  font-weight: 500;
}

.alarm-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.col-title {
  text-align: center;
}

.alarm-content {
  color: #666;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-name {
  font-weight: 500;
}

.zone-info {
  color: #409eff;
  font-size: 12px;
  margin-top: 2px;
}

.col-device-type .device-type {
  color: #666;
  font-size: 13px;
}

.time-main {
  margin-bottom: 2px;
}

.time-relative {
  color: #666;
  font-size: 12px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-pending {
  background: #fff2e8;
  color: #fa8c16;
}

.status-confirmed {
  background: #e6f7ff;
  color: #1890ff;
}

.status-in_progress {
  background: #fff7e6;
  color: #fa8c16;
}

.status-resolved {
  background: #f6ffed;
  color: #52c41a;
}

.status-completed {
  background: #f6ffed;
  color: #52c41a;
}

.action-btn {
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 4px;
}

.action-btn.confirm {
  background: #1890ff;
  color: white;
}

.action-btn.resolve {
  background: #52c41a;
  color: white;
}

.action-btn.detail {
  background: #f0f0f0;
  color: #666;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #f0f0f0;
}

.pagination-info {
  display: flex;
  align-items: center;
}

.total-info {
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  min-height: 32px;
}

.page-btn:hover:not(:disabled) {
  background: #f0f9ff;
  border-color: #1890ff;
  color: #1890ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.page-btn:active:not(:disabled) {
  transform: translateY(0);
}

.page-btn:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
  border-color: #e8e8e8;
}

.page-btn.active {
  background: #1890ff;
  border-color: #1890ff;
  color: white;
  font-weight: 500;
}

.page-btn.active:hover {
  background: #40a9ff;
  border-color: #40a9ff;
  color: white;
  transform: translateY(-1px);
}

.page-btn.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
  color: #999;
  font-weight: bold;
}

.page-btn.ellipsis:disabled {
  background: transparent;
  color: #999;
}

.btn-icon {
  transition: transform 0.2s;
}

.prev-btn:hover:not(:disabled) .btn-icon {
  transform: translateX(-2px);
}

.next-btn:hover:not(:disabled) .btn-icon {
  transform: translateX(2px);
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 8px;
}

.page-number-item {
  display: inline-block;
}

.page-number {
  min-width: 32px;
  padding: 6px 8px;
  justify-content: center;
}

.pagination-size {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-label {
  color: #666;
  font-size: 14px;
}

.page-size-select {
  padding: 6px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.page-size-select:hover {
  border-color: #40a9ff;
}

.page-size-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
}

/* 响应式适配 */
@media (max-width: 768px) {
  .pagination {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }
  
  .pagination-controls {
    order: 2;
    gap: 4px;
  }
  
  .pagination-info {
    order: 1;
  }
  
  .pagination-size {
    order: 3;
  }
  
  .page-btn {
    padding: 6px 10px;
    font-size: 13px;
  }
  
  .page-numbers {
    margin: 0 4px;
    gap: 2px;
  }
  
  .page-number {
    min-width: 28px;
    padding: 6px;
  }
  
  .prev-btn, .next-btn {
    padding: 8px;
  }
  
  .prev-btn .btn-icon,
  .next-btn .btn-icon {
    display: none;
  }
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal.large {
  max-width: 800px;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 8px 8px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  resize: vertical;
  box-sizing: border-box;
}

.alarm-info {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 0 0 8px 8px;
  position: sticky;
  bottom: 0;
  z-index: 10;
  flex-shrink: 0;
}

.footer-left {
  display: flex;
  gap: 8px;
}

.footer-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn.primary {
  background: #1890ff;
  color: white;
}

.btn.secondary {
  background: #f0f0f0;
  color: #666;
}

.btn.warning {
  background: #fa8c16;
  color: white;
}

.btn.warning:hover {
  background: #d46b08;
}

.btn.warning:disabled {
  background: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}

.btn.info {
  background: #1890ff;
  color: white;
}

.btn.info:hover {
  background: #096dd9;
}

.btn.info:disabled {
  background: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}

/* 告警详情样式 */
.detail-section {
  margin-bottom: 24px;
}

.detail-section h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.detail-item {
  display: flex;
  align-items: center;
}

.detail-item label {
  width: 80px;
  color: #666;
  margin-right: 12px;
  margin-bottom: 0;
}

.alarm-content-detail {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
  line-height: 1.5;
}

.alarm-meta {
  margin-top: 8px;
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
}

/* 高级处理对话框样式 */
.required {
  color: #f5222d;
}

.action-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.radio-option:hover {
  border-color: #1890ff;
  background: #f0f9ff;
}

.radio-option input[type="radio"] {
  margin: 0;
  margin-top: 2px;
}

.radio-text {
  font-weight: 500;
  color: #333;
}

.radio-option small {
  display: block;
  color: #666;
  font-size: 12px;
  margin-top: 4px;
}

.full-width {
  width: 100%;
}

.action-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
  transition: border-color 0.3s;
}

.action-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.action-select:hover {
  border-color: #40a9ff;
}

.photo-upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  transition: border-color 0.3s;
}

.photo-upload-area:hover {
  border-color: #1890ff;
}

.upload-zone {
  text-align: center;
  padding: 20px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.upload-zone:hover {
  background: #fafafa;
}

.upload-hint {
  color: #666;
}

.upload-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.selected-files {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.file-item {
  position: relative;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
  background: white;
}

.file-preview {
  width: 100%;
  height: 80px;
  object-fit: cover;
}

.file-info {
  padding: 8px;
}

.file-name {
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 11px;
  color: #666;
  margin-top: 2px;
}

.remove-file {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.uploaded-photos {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.uploaded-photo {
  position: relative;
}

.photo-preview {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
}

.remove-photo {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255, 0, 0, 0.7);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 12px;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr 1fr;
  }
  
  .filter-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 40px 1fr;
    gap: 8px;
  }
  
  .col-level,
  .col-device,
  .col-device-type,
  .col-time,
  .col-status {
    display: none;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

/* 通知系统样式 */
.notification-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

.notification {
  display: flex;
  align-items: center;
  min-width: 320px;
  max-width: 480px;
  margin-bottom: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-left: 4px solid transparent;
  opacity: 0;
  transform: translateX(100%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  cursor: pointer;
}

.notification-visible {
  opacity: 1;
  transform: translateX(0);
}

.notification-success {
  border-left-color: #52c41a;
  background: linear-gradient(135deg, #f6ffed 0%, #ffffff 100%);
}

.notification-error {
  border-left-color: #ff4d4f;
  background: linear-gradient(135deg, #fff2f0 0%, #ffffff 100%);
}

.notification-warning {
  border-left-color: #faad14;
  background: linear-gradient(135deg, #fffbe6 0%, #ffffff 100%);
}

.notification-info {
  border-left-color: #1890ff;
  background: linear-gradient(135deg, #e6f7ff 0%, #ffffff 100%);
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 12px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
}

.notification-success .notification-icon {
  background: #52c41a;
  color: white;
}

.notification-error .notification-icon {
  background: #ff4d4f;
  color: white;
}

.notification-warning .notification-icon {
  background: #faad14;
  color: white;
}

.notification-info .notification-icon {
  background: #1890ff;
  color: white;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-message {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
  word-wrap: break-word;
}

.notification-close {
  margin-left: 12px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.notification-close:hover {
  opacity: 1;
  color: #666;
}

/* 动画效果 */
.notification-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 响应式适配 */
@media (max-width: 768px) {
  .notification-container {
    top: 10px;
    right: 10px;
    left: 10px;
  }
  
  .notification {
    min-width: auto;
    margin-bottom: 8px;
  }
}

/* 处理信息样式 */
.processing-info {
  background: #fafafa;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}

.resolve-action-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.action-test_alarm {
  background: #1890ff;
}

.action-false_alarm {
  background: #faad14;
}

.action-real_alarm {
  background: #52c41a;
}

.resolve-note {
  margin-top: 16px;
}

.resolve-note label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.resolve-note-content {
  background: white;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.resolve-photos {
  margin-top: 16px;
}

.resolve-photos label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.photo-thumbnail {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
}

.photo-thumbnail:hover {
  transform: scale(1.05);
}

.photo-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.photo-thumbnail:hover .photo-overlay {
  opacity: 1;
}

.zoom-icon {
  font-size: 24px;
  color: white;
}

/* 照片查看器样式 */
.photo-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-viewer {
  width: 90vw;
  height: 90vh;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.photo-viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
}

.photo-counter {
  font-size: 14px;
  font-weight: 500;
}

.photo-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 32px;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.photo-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.photo-viewer-content {
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  min-height: 0;
}

.photo-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 0;
}

.photo-main {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.photo-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 48px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  z-index: 10;
}

.photo-nav-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

.prev-btn {
  left: 20px;
}

.next-btn {
  right: 20px;
}

.photo-thumbnails {
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  gap: 8px;
  overflow-x: auto;
  justify-content: center;
}

.thumbnail-item {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;
  flex-shrink: 0;
}

.thumbnail-item.active {
  border-color: #1890ff;
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .photo-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 8px;
  }
  
  .photo-thumbnail {
    width: 100px;
    height: 100px;
  }
  
  .photo-viewer {
    width: 95vw;
    height: 95vh;
  }
  
  .photo-nav-btn {
    width: 50px;
    height: 50px;
    font-size: 36px;
  }
  
  .prev-btn {
    left: 10px;
  }
  
  .next-btn {
    right: 10px;
  }
  
  .photo-thumbnails {
    padding: 12px;
  }
  
  .thumbnail-item {
    width: 50px;
    height: 50px;
  }
}

/* 处理时间轴样式 */
.processing-timeline {
  position: relative;
  padding-left: 40px;
}

.timeline-item {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-item::before {
  content: '';
  position: absolute;
  left: -28px;
  top: 32px;
  bottom: -24px;
  width: 2px;
  background: #e8e8e8;
}

.timeline-item:last-child::before {
  display: none;
}

.timeline-marker {
  position: absolute;
  left: -44px;
  top: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: white;
  font-weight: bold;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.timeline-icon span {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  width: 100%;
  height: 100%;
}

.timeline-icon.icon-confirm {
  background: #52c41a;
}

.timeline-icon.icon-process {
  background: #1890ff;
}

.timeline-icon.icon-close {
  background: #666;
}

.timeline-icon.icon-reopen {
  background: #fa8c16;
}

.timeline-icon.icon-escalate {
  background: #722ed1;
}

.timeline-content {
  flex: 1;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.timeline-header {
  margin-bottom: 12px;
}

.timeline-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.timeline-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
}

.timeline-user {
  font-weight: 500;
}

.timeline-time {
  color: #999;
}

.timeline-note {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
  line-height: 1.5;
  color: #333;
  margin-bottom: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.timeline-photos {
  margin-top: 12px;
}

.timeline-photos .photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  max-width: 400px;
}

.photo-thumbnail.small {
  width: 80px;
  height: 80px;
}

.processing-action-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  color: white;
}

.processing-action-badge.action-test_alarm {
  background: #1890ff;
}

.processing-action-badge.action-false_alarm {
  background: #faad14;
}

.processing-action-badge.action-real_alarm {
  background: #52c41a;
}

.processing-action-badge.action-confirm {
  background: #52c41a;
}

.processing-action-badge.action-close {
  background: #666;
}

.processing-action-badge.action-reopen {
  background: #fa8c16;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .processing-timeline {
    padding-left: 32px;
  }
  
  .timeline-marker {
    left: -32px;
  }
  
  .timeline-item::before {
    left: -20px;
  }
  
  .timeline-icon {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
  
  .timeline-content {
    padding: 12px;
  }
  
  .timeline-meta {
    flex-direction: column;
    gap: 4px;
  }
  
  .timeline-photos .photo-grid {
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    gap: 6px;
  }
  
  .photo-thumbnail.small {
    width: 60px;
    height: 60px;
  }
}
</style>